<script>
    // Layout
    import Authenticated from "@/Layouts/Authenticated.vue";

    // Packages
    import pickBy from 'lodash/pickBy';
    import {Head} from "@inertiajs/vue3";
    import debounce from 'lodash/debounce';
    import mapValues from 'lodash/mapValues';

    // Components 
    import Button from "@/Components/Button.vue";
    import Pagination from "@/Components/Pagination.vue";
    import SettingsTabs from "@/Components/SettingsTabs.vue";
    import SearchFilter from "@/Components/SearchFilter.vue";
    
    import CreateBankDetails from "@/Components/SlideOuts/CreateBankDetails.vue";

    // Icons
    import {PlusSmIcon as PlusSmIconSolid} from '@heroicons/vue/solid'


    export default {
        layout: Authenticated,
        components: {
            Head,
            Button,
            Pagination,
            SettingsTabs,
            SearchFilter,
            PlusSmIconSolid,
            CreateBankDetails
        },
        props: ['bank_details'],
        data() {
            return {
                form: {
                    search: null
                }
            }
        },
        methods: {
            toggleCreateBankDetailsSlideOut() {
                this.$refs.createBankDetailsSlideOut.updating = false;
                this.$refs.createBankDetailsSlideOut.open();
            },
            editBankDetailsSlideout(bank_detail) {
                this.$refs.createBankDetailsSlideOut.updating = true;
                this.$refs.createBankDetailsSlideOut.form.id = bank_detail.id;
                this.$refs.createBankDetailsSlideOut.form.bank = bank_detail.bank;
                this.$refs.createBankDetailsSlideOut.form.branch_address = bank_detail.branch_address;
                this.$refs.createBankDetailsSlideOut.form.account_name = bank_detail.account_name;
                this.$refs.createBankDetailsSlideOut.form.sort_code = bank_detail.sort_code;
                this.$refs.createBankDetailsSlideOut.form.account_number = bank_detail.account_number;
                this.$refs.createBankDetailsSlideOut.form.bic = bank_detail.bic;
                this.$refs.createBankDetailsSlideOut.form.swift = bank_detail.swift;
                this.$refs.createBankDetailsSlideOut.form.iban = bank_detail.iban;
                this.$refs.createBankDetailsSlideOut.form.routing = bank_detail.routing;
                this.$refs.createBankDetailsSlideOut.open();
            },
            deleteBankDetails(id) {
                this.$swal.fire({
                    title: 'Remove Bank Details',
                    html: "You are removing bank details. <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.$inertia.delete(route('admin.settings.bank-details.bank-detail.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
            },
            reset() {
                this.form = mapValues(this.form, () => null)
            },
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('admin.settings.bank-details.index'), pickBy(this.form), {preserveState: true})
                }, 500),
            },
        },

    }
</script>


<template>
    <Head title="Bank Details"/>
    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Bank Details</h1>
    <SettingsTabs/>
    <div class="bg-white py-6 px-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4" @reset="reset"></search-filter>
            <Button v-on:click="toggleCreateBankDetailsSlideOut"
                class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New Bank Details
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Bank
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Account Name
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Sort Code
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Account Number
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Edit</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="bank_details.data.length > 0" v-for="(bank_detail, index) in bank_details.data" :key="index" :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="editBankDetailsSlideout(bank_detail)">
                                        {{ bank_detail.bank }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="editBankDetailsSlideout(bank_detail)">
                                        {{ bank_detail.account_name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="editBankDetailsSlideout(bank_detail)">
                                        {{ bank_detail.sort_code }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="editBankDetailsSlideout(bank_detail)">
                                        {{ bank_detail.account_number }}
                                    </td>
                                    <td class="px-6  py-4 whitespace-nowrap text-right text-sm">
                                        <span class="text-red-600 hover:text-red-900 float-right" @click="deleteBankDetails(bank_detail)">
                                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                                </path>
                                            </svg>
                                        </span>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="3" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                        There are no bank details available for your search criteria.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="bank_details.links"/>
        </div>
    </div>
    <CreateBankDetails ref="createBankDetailsSlideOut"/>
</template>